/**
 * @voltxampere/ui — V/div standard scope values (Sprint 15)
 *
 * Per-channel volts-per-division ladder. Same 1-2-5 sequence as
 * the time base, ranging from 100 µV/div to 20 V/div (covers
 * Sprint 15's expected analog domain — sub-mV ripple to mains-
 * adjacent supplies).
 *
 * `autoFit` chooses the smallest standard value whose 8-division
 * vertical extent encloses the sample peak-to-peak — the classic
 * "auto" button behaviour every benchtop scope ships.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
import { V_DIVISION_COUNT } from "./types.js";
/** Standard volts-per-division values, ascending. */
export const STANDARD_V_DIV_VALUES = [
    1e-4, 2e-4, 5e-4, 1e-3, 2e-3, 5e-3, 1e-2, 2e-2, 5e-2, 1e-1, 2e-1, 5e-1, 1, 2, 5, 10, 20,
];
export const MIN_V_DIV = STANDARD_V_DIV_VALUES[0] ?? 1e-4;
export const MAX_V_DIV = STANDARD_V_DIV_VALUES[STANDARD_V_DIV_VALUES.length - 1] ?? 20;
/**
 * Snap to the nearest standard V/div on a log scale. Ties prefer
 * the smaller value (more resolution).
 */
export function snapVDiv(volts) {
    if (!Number.isFinite(volts) || volts <= 0)
        return MIN_V_DIV;
    if (volts <= MIN_V_DIV)
        return MIN_V_DIV;
    if (volts >= MAX_V_DIV)
        return MAX_V_DIV;
    let best = STANDARD_V_DIV_VALUES[0] ?? MIN_V_DIV;
    let bestDelta = Math.abs(Math.log10(volts) - Math.log10(best));
    for (const v of STANDARD_V_DIV_VALUES) {
        const delta = Math.abs(Math.log10(volts) - Math.log10(v));
        if (delta < bestDelta) {
            best = v;
            bestDelta = delta;
        }
    }
    return best;
}
export function stepVDiv(current, direction) {
    const snapped = snapVDiv(current);
    const idx = STANDARD_V_DIV_VALUES.findIndex((v) => v === snapped);
    if (idx === -1)
        return snapped;
    const target = direction === "in" ? idx - 1 : idx + 1;
    if (target < 0)
        return STANDARD_V_DIV_VALUES[0] ?? snapped;
    if (target >= STANDARD_V_DIV_VALUES.length) {
        return STANDARD_V_DIV_VALUES[STANDARD_V_DIV_VALUES.length - 1] ?? snapped;
    }
    return STANDARD_V_DIV_VALUES[target] ?? snapped;
}
/**
 * Pick the smallest standard V/div whose `divCount` vertical
 * extent encloses the supplied peak-to-peak voltage. Used by the
 * waveform's "auto" button (`0` keyboard shortcut). Empty / non-
 * finite input falls back to the smallest standard value so the
 * scope shows the noise floor instead of black.
 */
export function autoFitVDiv(vpp, divCount = V_DIVISION_COUNT, marginFactor = 1.2) {
    if (!Number.isFinite(vpp) || vpp <= 0 || divCount <= 0)
        return MIN_V_DIV;
    // We want vpp * marginFactor / divCount ≤ V/div.
    const required = (vpp * marginFactor) / divCount;
    if (required <= MIN_V_DIV)
        return MIN_V_DIV;
    if (required >= MAX_V_DIV)
        return MAX_V_DIV;
    for (const v of STANDARD_V_DIV_VALUES) {
        if (v >= required)
            return v;
    }
    return MAX_V_DIV;
}
//# sourceMappingURL=v-div.js.map